/**
 * The Body Field — entry.
 *
 * Two modes:
 *
 *  MANUAL (debug)  — the existing animation dropdown + breathing HUD.
 *                    Toggle with the D key. Used for catalog curation and
 *                    breathing preset calibration.
 *
 *  DIRECTOR        — LLM-driven performance. A prompt form is shown on first
 *                    paint; submitting it calls the Cloud Run director service,
 *                    which returns a stream of Beats that the BeatPlayer drains
 *                    onto the figure. A re-prompt button lets you change the
 *                    prompt mid-session.
 *
 * The 3D scene (figure + breathing) starts regardless of mode so the character
 * is already alive when the prompt form is visible.
 */

import {
  ACESFilmicToneMapping,
  Box3,
  Clock,
  Color,
  DirectionalLight,
  EquirectangularReflectionMapping,
  HemisphereLight,
  Texture,
  type Material,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PCFSoftShadowMap,
  PerspectiveCamera,
  PlaneGeometry,
  PMREMGenerator,
  Scene,
  ShadowMaterial,
  SkinnedMesh,
  SRGBColorSpace,
  TextureLoader,
  Vector3,
  WebGLRenderer,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { GroundedSkybox } from 'three/examples/jsm/objects/GroundedSkybox.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

import { SparkRenderer, SplatMesh, SplatEdit, SplatEditSdf, SplatEditRgbaBlendMode, SplatEditSdfType } from '@sparkjsdev/spark';

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  HULL_CHARACTER_SCALE,
  hullCharacterFootY,
  hullCharacterWorldZ,
} from './scene/hull-footing';
import {
  attachHullShadowCatchers,
  detachHullShadowCatchers,
  syncHullSunOverFeet,
} from './scene/hull-shadow';
import { loadFigure, type Figure } from './motion/figure';
import { BeatPlayer } from './director/beat-player';
import { DirectorClient, type DirectorState } from './director/director-client';
import { AmbientPlayer, type AmbientUiState } from './director/ambient-player';
import { WorldLoader } from './director/world-loader';
import { NarrationPlayer } from './director/narration-player';
import { CacheBrowser } from './ui/cache-browser';
import { createTuningPanel, SPLAT_TRANSFORM_DEFAULTS, SPLAT_TRANSFORM_LS_KEY, type SplatTransform } from './director/tuning-panel';
import { createSceneDirectorPanel } from './director/scene-director-panel';
import { extractPose, findClips } from './dev/extract-pose';
import type { Beat } from './director/types';

const BACKGROUND = '#fafaf7';

const CHARACTERS_MANIFEST_URL = './models/characters.json';
const ANIMS_MANIFEST_URL = './models/anims.json';
const FIGURE_URL_FALLBACK = './models/characters/Lewis.glb';

const DEFAULT_CLIP_MATCHERS: RegExp[] = [
  /^Standing Idle Unarmed$/i,
  /^Standing Idle$/i,
  /idle/i,
];
const BREATHING_CLIP_MATCH = /^Standing Idle$/i;
const BREATHING_BPM_DEFAULT = 14;
const BREATHING_AMP_DEFAULT = 0.12;

const BREATHING_PRESETS: { label: string; bpm: number; amplitude: number }[] = [
  { label: 'Calm', bpm: 9, amplitude: 0.55 },
  { label: 'Neutral', bpm: 15, amplitude: 0.4 },
  { label: 'Tense', bpm: 22, amplitude: 0.3 },
  { label: 'Nervous', bpm: 28, amplitude: 0.25 },
  { label: 'Winded', bpm: 32, amplitude: 0.7 },
];

/**
 * Director server URL. Set VITE_DIRECTOR_URL in .env.local for dev:
 *   VITE_DIRECTOR_URL=http://localhost:8080
 * In production this is the Cloud Run service URL.
 */
const DIRECTOR_URL: string =
  (import.meta as unknown as { env: Record<string, string> }).env
    ?.VITE_DIRECTOR_URL ?? 'http://localhost:8080';

/**
 * Pin the runtime character to the exact rig that's registered with Uthana.
 * When set, overrides the first entry in characters.json on initial load.
 *   VITE_CHARACTER_URL=./models/characters/<UTHANA_CHARACTER_ID>.glb
 * This guarantees Uthana retargeted clips play on the same rig they were
 * authored against (eliminates bind-pose / bone-rest mismatches).
 */
const PINNED_CHARACTER_URL: string | null =
  (import.meta as unknown as { env: Record<string, string> }).env
    ?.VITE_CHARACTER_URL ?? null;

async function boot(): Promise<void> {
  const container = document.getElementById('app');
  if (!container) throw new Error('No #app container');

  const renderer = new WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.setClearColor(new Color(BACKGROUND), 1);
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.65;
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFSoftShadowMap;
  container.appendChild(renderer.domElement);

  const scene = new Scene();
  scene.background = new Color(BACKGROUND);

  // ── (3) Image-based lighting (IBL) ────────────────────────────────────────
  // RoomEnvironment generates a neutral studio PMREM texture at zero runtime
  // cost — no HDR file download needed. All PBR materials in the scene (the
  // character's skin, metal accessories, etc.) will use it for reflections.
  const pmremGenerator = new PMREMGenerator(renderer);
  scene.environment = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;
  pmremGenerator.dispose();

  // ── (2) Three-point lighting ──────────────────────────────────────────────
  // Hemi is kept but at reduced intensity — IBL now handles ambient.
  const hemi = new HemisphereLight('#fff7e6', '#cfd6dd', 0.4);
  hemi.position.set(0, 4, 0);
  scene.add(hemi);

  // Key light — warm, upper-right. Casts the main shadow.
  const key = new DirectionalLight('#fff4e0', 0.8);
  key.position.set(2.5, 3.5, 2.0);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.camera.near = 0.5;
  key.shadow.camera.far = 20;
  key.shadow.camera.left   = -3;
  key.shadow.camera.right  =  3;
  key.shadow.camera.top    =  5;
  key.shadow.camera.bottom = -1;
  key.shadow.bias = -0.002;

  // Hull-only shadow driver (intensity 0 — shadow map only, like skybox-clone-test).
  // Profile mode uses `key` + `shadowReceiverPlane`; hull receives on mesh triangles.
  const hullSun = new DirectionalLight('#ffffff', 0);
  hullSun.castShadow = true;
  hullSun.shadow.mapSize.set(1024, 1024);
  hullSun.shadow.camera.near = 0.5;
  hullSun.shadow.camera.far = 18;
  hullSun.shadow.camera.left = -4;
  hullSun.shadow.camera.right = 4;
  hullSun.shadow.camera.top = 4;
  hullSun.shadow.camera.bottom = -4;
  hullSun.shadow.bias = -0.0005;
  hullSun.shadow.intensity = 0.45;
  scene.add(hullSun);
  scene.add(hullSun.target);

  scene.add(key);

  // Fill light — cool, left side, low intensity. Lifts the shadow side.
  const fill = new DirectionalLight('#c8d8f0', 0.25);
  fill.position.set(-2.5, 1.5, 1.0);
  scene.add(fill);

  // Rim light — behind and above, separates the character from the background.
  const rim = new DirectionalLight('#ffffff', 0.35);
  rim.position.set(0, 3.5, -3.0);
  scene.add(rim);

  const shadowReceiverPlaneGeo = new PlaneGeometry(12, 12);
  const shadowReceiverPlaneMat = new ShadowMaterial({ opacity: 0.25 });
  const shadowReceiverPlane = new Mesh(shadowReceiverPlaneGeo, shadowReceiverPlaneMat);
  shadowReceiverPlane.rotation.x = -Math.PI / 2;
  shadowReceiverPlane.position.y = 0;
  shadowReceiverPlane.receiveShadow = true;
  scene.add(shadowReceiverPlane);

  // Camera + look-at anchor — declared before load helpers reference it above.
  const camera = new PerspectiveCamera(
    32,
    container.clientWidth / container.clientHeight,
    0.05,
    500,
  );
  // Profile framing ~3.5 m from figure at origin (+Z axis). Kept symmetrical
  // with the authored Skybox hull at world origin instead of coupling camera XZ to mesh shift.
  camera.position.set(0, 1.07, 3.506);
  const target = new Vector3(0, 1.0, 0);
  camera.lookAt(target);

  // Global orbit camera (drag rotates view, not the character). Used in all modes.
  const orbitControls = new OrbitControls(camera, renderer.domElement);
  orbitControls.enableZoom = false;
  orbitControls.enablePan = false;
  orbitControls.enableDamping = true;
  orbitControls.dampingFactor = 0.08;
  orbitControls.rotateSpeed = -0.3;
  orbitControls.target.copy(target);
  orbitControls.update();

  // ── Spark Gaussian Splat renderer ───────────────────────────────────────
  // SparkRenderer hooks into the Three.js pipeline. Adding it to the scene
  // is all that's needed — it handles GPU-accelerated splat sorting and
  // correct compositing against normal Three.js meshes (the character).
  const sparkRenderer = new SparkRenderer({ renderer });
  scene.add(sparkRenderer);

  // Active SplatMesh — replaced whenever a new WorldLabs world is ready or
  // a cached splat is loaded from the dropdown.
  let activeSplat: SplatMesh | null = null;

  // Splat transform — loaded from localStorage, kept in sync with the
  // World tuning panel sliders, applied to every new SplatMesh on load.
  function loadSplatTransform(): SplatTransform {
    try {
      const stored = localStorage.getItem(SPLAT_TRANSFORM_LS_KEY);
      if (stored) return { ...SPLAT_TRANSFORM_DEFAULTS, ...(JSON.parse(stored) as Partial<SplatTransform>) };
    } catch { /* ignore */ }
    return { ...SPLAT_TRANSFORM_DEFAULTS };
  }

  let splatTransform: SplatTransform = loadSplatTransform();

  // ── Near-wall exclusion zone (SplatEdit) ────────────────────────────────
  // A sphere-shaped SplatEdit with opacity=0 + MULTIPLY erases all Gaussians
  // inside it. We position it on the camera every frame so the near walls of
  // the room are always removed from around the viewpoint.
  // Radius is controlled by the "Near R" slider (splatTransform.clipW).
  // Added to the scene root so it applies globally to every SplatMesh.
  const cameraExclusionEdit = new SplatEdit({
    rgbaBlendMode: SplatEditRgbaBlendMode.MULTIPLY,
  });
  const cameraExclusionSphere = new SplatEditSdf({
    type: SplatEditSdfType.SPHERE,
    opacity: 0,  // opacity 0 × MULTIPLY = delete all Gaussians inside
    radius: splatTransform.clipW,
  });
  cameraExclusionEdit.add(cameraExclusionSphere);
  scene.add(cameraExclusionEdit);

  function applyTransformToSplat(splat: SplatMesh, t: SplatTransform): void {
    splat.scale.setScalar(t.scale);
    splat.position.set(t.posX, t.posY, t.posZ);
    splat.rotation.y = (t.rotY * Math.PI) / 180;
    cameraExclusionSphere.radius = t.clipW;
  }

  function loadSplat(splatUrl: string, openCvCorrection = false): void {
    if (activeSplat) {
      scene.remove(activeSplat);
      activeSplat = null;
    }
    const splat = new SplatMesh({ url: splatUrl });
    // WorldLabs API returns splats in OpenCV coords (Y-down). The Marble web UI
    // applies the correction before export; the raw API URLs do not.
    // Rotating 180° around X is the standard OpenCV→OpenGL transform.
    if (openCvCorrection) splat.rotation.x = Math.PI;
    applyTransformToSplat(splat, splatTransform);
    scene.add(splat);
    activeSplat = splat;
    console.log('[world] loading splat from', splatUrl);
  }

  function clearSplat(): void {
    if (activeSplat) {
      scene.remove(activeSplat);
      activeSplat = null;
    }
  }

  function onSplatTransformChange(t: SplatTransform): void {
    splatTransform = t;
    if (activeSplat) applyTransformToSplat(activeSplat, t);
  }

  // ── Skybox AI (Blockade Labs) world geometry ──────────────────────────
  // The Skybox AI path produces two assets:
  //   - panorama JPG    → wrapped as a GroundedSkybox dome around the scene
  //   - optional GLB    → depth-displaced world mesh (needs API mesh export entitlement)
  //
  // The GroundedSkybox is what gives the character a real floor to plant on.
  // Without it the panorama would be rendered at infinity and the character
  // would appear to float — see the May 23 design notes for the long version.
  let activeGround: Object3D | null = null;
  let activeSkybox: GroundedSkybox | null = null;
  /**
   * Bumped whenever a new pano-load starts or a GLB / clearWorld wins over the dome.
   * In-flight `loadPanoramaBackground` completions with a stale epoch must not call
   * `GroundedSkybox` (avoids dome-over-mesh races when panoramas decode slowly).
   */
  let panoBackdropEpoch = 0;

  // Reusable loaders. Keeping a single TextureLoader and GLTFLoader avoids
  // per-call construction and lets the browser de-dup any in-flight requests
  // for the same URL.
  const textureLoader = new TextureLoader();
  const gltfLoader = new GLTFLoader();

  /**
   * GroundedSkybox parameters tuned for Skybox AI panoramas.
   *
   *   SKYBOX_CAMERA_HEIGHT: the height the original 360° photo was taken
   *     from. Skybox AI panoramas are consistent with a standing-eye-level
   *     camera — ~1.6m. This is what aligns the dome's painted ground with
   *     world y=0 so the character's feet plant correctly.
   *   SKYBOX_RADIUS: how far the floor disc extends before curving up into
   *     the dome. Must comfortably contain the scene camera at all zoom
   *     levels (camera sits ~3.5m from origin on +Z and follows the character).
   *     Larger values hide the floor-to-dome seam better at oblique angles.
   *
   * Caveat: GroundedSkybox flattens the lower hemisphere of the panorama
   * onto the floor disc. Foreground objects in the panorama (a person
   * standing close to the original camera, furniture) get visibly squished
   * as a result. The fix for that is prompt engineering, not geometry.
   */
  const SKYBOX_CAMERA_HEIGHT = 1.6;
  const SKYBOX_RADIUS = 20;

  /**
   * Wrap an equirectangular panorama JPG into a GroundedSkybox dome. The
   * dome's lower portion smoothly squashes into a flat floor disc at world
   * y=0, so the character at y=0 stands on the painted ground instead of
   * floating above an infinite-projection backdrop.
   *
   * We deliberately do NOT also assign the texture to `scene.environment`
   * — the character's IBL stays neutral (RoomEnvironment) so the figure
   * reads consistently regardless of which scene they're dropped into.
   */
  async function loadPanoramaBackground(panoUrl: string): Promise<void> {
    panoBackdropEpoch += 1;
    const epoch = panoBackdropEpoch;
    shadowReceiverPlane.visible = true;

    const tex = await textureLoader.loadAsync(panoUrl);
    if (epoch !== panoBackdropEpoch) {
      tex.dispose();
      return;
    }

    tex.colorSpace = SRGBColorSpace;
    // Skybox AI ships an 8K equirectangular JPG. Anisotropic filtering keeps
    // the periphery sharp at glancing angles; without it the dome blurs as
    // the camera pans across the equator.
    tex.anisotropy = renderer.capabilities.getMaxAnisotropy();

    clearSkybox();

    const skybox = new GroundedSkybox(tex, SKYBOX_CAMERA_HEIGHT, SKYBOX_RADIUS);
    skybox.position.y = SKYBOX_CAMERA_HEIGHT;
    // The pano JPG is already a baked LDR photo. Opting out of the renderer's
    // ACES Filmic + 0.65 exposure pipeline (which is tuned for HDR PBR
    // characters) so the dome renders 1:1 as authored — same colours and
    // contrast as Skybox AI's own web viewer.
    const skyboxMat = skybox.material as MeshBasicMaterial;
    skyboxMat.toneMapped = false;
    scene.add(skybox);
    activeSkybox = skybox;
    console.log('[world] grounded skybox applied');
  }

  /**
   * Equirect pano on `scene.background` for hull mode (Skybox viewer style).
   * Unlike {@link loadPanoramaBackground}, no GroundedSkybox dome — the hull
   * GLB provides depth; the JPG fills the sky through the mesh openings.
   */
  async function applyHullEquirectBackdrop(panoUrl: string): Promise<void> {
    const tex = await textureLoader.loadAsync(panoUrl);
    tex.colorSpace = SRGBColorSpace;
    tex.mapping = EquirectangularReflectionMapping;
    tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
    clearSkybox();
    const prev = scene.background;
    if (prev && prev instanceof Texture) prev.dispose();
    scene.background = tex;
    console.log('[world] hull equirect backdrop applied');
  }

  /**
   * Load a Skybox AI GLB world shell as authored — no bottom flatten, no floor
   * snap, no Z re-centre (matches the skybox-clone test page). Foot placement
   * uses {@link hullCharacterFootY} raycast on the supplied geometry.
   *
   * Compositing:
   *   - Drop any active `GroundedSkybox` when this loads.
   *   - Hide the flat `shadowReceiverPlane`; hull meshes receive cast shadows.
   */
  async function loadGroundMesh(glbUrl: string, backdropPanoUrl?: string): Promise<void> {
    panoBackdropEpoch += 1;
    const gltf = await gltfLoader.loadAsync(glbUrl);
    const mesh = gltf.scene;
    mesh.position.set(0, 0, 0);
    mesh.updateMatrixWorld(true);

    // The hull is a textured photographic shell — its diffuse map already has
    // outdoor lighting baked in by Blockade. PBR shading would re-light it
    // with the character's RoomEnvironment IBL (a neutral indoor cube) and
    // tone-map it through ACES + 0.65 exposure, both of which mute and
    // darken the look versus Skybox AI's own viewer. Swap each material to
    // an unlit MeshBasic over the same diffuse texture, opt out of tone
    // mapping, and bump anisotropy so the texture stays sharp at glancing
    // angles when the camera pans across it. The mesh-only toggle caches
    // material refs *after* this swap, so its grey+wire mode still works.
    const maxAniso = renderer.capabilities.getMaxAnisotropy();
    mesh.traverse((node) => {
      const m = node as Mesh;
      if (!m.isMesh) return;
      const original = m.material;
      const list = Array.isArray(original) ? original : [original];
      const replaced = list.map((src) => {
        const std = src as MeshStandardMaterial;
        if (std.map) {
          std.map.colorSpace = SRGBColorSpace;
          std.map.anisotropy = maxAniso;
        }
        const basic = new MeshBasicMaterial({
          map: std.map ?? null,
          color: std.color ?? new Color(0xffffff),
          side: std.side,
          transparent: std.transparent,
          opacity: std.opacity,
          alphaTest: std.alphaTest,
          alphaMap: std.alphaMap ?? null,
          depthWrite: std.depthWrite,
          depthTest: std.depthTest,
        });
        basic.toneMapped = false;
        basic.name = std.name;
        return basic;
      });
      m.material = Array.isArray(original) ? replaced : replaced[0]!;
    });
    attachHullShadowCatchers(mesh);

    const box = new Box3().setFromObject(mesh);

    // Only discard the pano dome after the GLB is ready — avoids a blank sky if
    // the download fails halfway through.
    clearSkybox();
    shadowReceiverPlane.visible = false;

    if (activeGround) {
      detachHullShadowCatchers(activeGround);
      scene.remove(activeGround);
      activeGround = null;
    }
    scene.add(mesh);
    activeGround = mesh;
    mesh.updateMatrixWorld(true);

    // Footing + hull camera before pano download — avoids the character sitting
    // at profile (0,0,0) while the backdrop texture loads.
    enterHullSkyboxMode();

    // If the user toggled mesh-only mode on while this hull was still
    // generating, retroactively apply the gray + wireframe treatment so
    // the world matches the figure. No-op when meshOnlyMode is false.
    reapplyMeshOnlyForCurrentGround();
    const size = new Vector3();
    box.getSize(size);
    console.log(
      `[world] Skybox hull as-supplied — bbox (${size.x.toFixed(1)}, ${size.y.toFixed(1)}, ${size.z.toFixed(1)}) m`,
    );
    if (backdropPanoUrl) await applyHullEquirectBackdrop(backdropPanoUrl);
  }

  function clearGround(): void {
    if (activeGround) {
      detachHullShadowCatchers(activeGround);
      scene.remove(activeGround);
      activeGround = null;
    }
    shadowReceiverPlane.visible = true;
    exitHullSkyboxMode();
  }

  /** Remove the GroundedSkybox dome and dispose its GPU resources. */
  function clearSkybox(): void {
    if (!activeSkybox) return;
    scene.remove(activeSkybox);
    activeSkybox.geometry.dispose();
    const mat = activeSkybox.material;
    const materials = Array.isArray(mat) ? mat : [mat];
    for (const m of materials) {
      const basic = m as MeshBasicMaterial;
      if (basic.map) basic.map.dispose();
      m.dispose();
    }
    activeSkybox = null;
  }

  /** Reset every world-environment layer back to the neutral default. */
  function clearWorld(): void {
    panoBackdropEpoch += 1;
    clearSplat();
    clearGround();
    clearSkybox();
    scene.background = new Color(BACKGROUND);
  }

  // ── Character manifest ──────────────────────────────────────────────────
  // `rigProfile` controls clip-track binding inside loadFigure. Mixamo /
  // Avaturn / RPM characters omit it (default behaviour); cc-base characters
  // (Character Creator 4 exports like Riya) set `"rigProfile": "cc-base"`
  // so HY-Motion's Mixamo-named tracks get renamed to CC_Base_* at clip-load.
  //
  // `skeletonFamily` is the human-readable label for the source pipeline
  // (Mixamo / CharacterCreator4 / MetaHuman / …). It does not branch any
  // code — it's surfaced in the dropdown and dev logs so the team knows
  // which pipeline a character came from. See `SkeletonFamily` in
  // `motion/rig-profiles.ts` for the canonical list.
  type ManifestEntry = {
    name: string;
    url: string;
    voiceId?: string;
    gender?: string;
    rigProfile?: 'mixamo' | 'avaturn' | 'cc-base' | 'cc-base-noprefix' | 'rigify-stretch' | 'metahuman' | 'epic-skeleton';
    skeletonFamily?:
      | 'Mixamo'
      | 'Avaturn'
      | 'ReadyPlayerMe'
      | 'CharacterCreator4'
      | 'MetaHuman'
      | 'UnityHumanoid'
      | 'Rigify'
      | 'EpicSkeleton'
      | 'Custom';
    /**
     * Suppresses this entry in the character-selector dropdown but keeps it
     * loadable via VITE_CHARACTER_URL or any code path that addresses the
     * URL directly (so we don't lose the wiring while a character is parked
     * in WIP). Flip to `false` / remove the key to surface it again.
     */
    hidden?: boolean;
    /**
     * `cc-base` characters only. Hide one or more CC body-skin slots
     * after load so residual pose-driven clothing skinning gaps can't
     * expose skin through the seam. See `hideBodySkinSlots` in
     * `cc-glb-materials.ts` for per-slot coverage caveats.
     *
     * Examples:
     *   - Riya: `["body"]`  (high-collar shirt fully covers torso)
     *   - Anne: `["leg"]`   (jeans + heels fully cover legs)
     */
    hideSkinSlots?: ('body' | 'arm' | 'leg' | 'head')[];
  };
  let charactersManifest: ManifestEntry[] = [];
  try {
    const res = await fetch(CHARACTERS_MANIFEST_URL);
    if (res.ok) charactersManifest = (await res.json()) as ManifestEntry[];
  } catch (err) {
    console.warn('[main] failed to load characters manifest', err);
  }

  const initialCharacterUrl =
    PINNED_CHARACTER_URL ??
    charactersManifest[0]?.url ??
    FIGURE_URL_FALLBACK;
  if (PINNED_CHARACTER_URL) {
    console.log(`[main] character pinned via VITE_CHARACTER_URL: ${PINNED_CHARACTER_URL}`);
  }

  // ── Load initial character ──────────────────────────────────────────────
  // Initial yaw: `0` aims the model down +Z (characters face the camera on load).
  const baseYaw = 0;
  // Per-URL load options. Currently:
  //   • `rigProfile` — used to remap HY-Motion's Mixamo-named clip tracks
  //     onto the target rig's bone names (cc-base / metahuman / etc.).
  //     Mixamo / Avaturn / RPM characters have no entry → rigProfile
  //     undefined → no rename, original behaviour.
  //   • `hideSkinSlots` — cc-base characters whose clothing fully covers
  //     a body region (e.g. Riya's high-collar shirt → torso, Anne's
  //     full-length jeans → legs) opt in so residual skinning-weight
  //     gaps can't expose skin through the seam.
  function loadOptsFor(
    url: string,
  ): { rigProfile?: ManifestEntry['rigProfile']; hideSkinSlots?: ManifestEntry['hideSkinSlots'] } {
    const entry = charactersManifest.find((m) => m.url === url);
    return { rigProfile: entry?.rigProfile, hideSkinSlots: entry?.hideSkinSlots };
  }
  let figure: Figure = await loadFigure(initialCharacterUrl, loadOptsFor(initialCharacterUrl));
  // Console-accessible debug handles. `__figure` for direct inspection;
  // `__findClips` and `__extractPose` are for seeding procedural-hold
  // first-pass poses from real Mixamo/Avaturn clips. See
  // `src/dev/extract-pose.ts` for usage.
  const win = window as unknown as Record<string, unknown>;
  win['__figure'] = figure;
  win['__findClips'] = findClips;
  win['__extractPose'] = extractPose;
  win['__narrate'] = (text?: string) =>
    narrationPlayer.speak(text ?? promptTextareaEl?.value.trim() ?? '', currentVoiceId);

  // ── (4) Shadow casting on figure meshes ──────────────────────────────────
  function enableShadowCasting(root: Object3D): void {
    root.traverse((node) => {
      if ((node as Mesh).isMesh) {
        (node as Mesh).castShadow = true;
      }
    });
  }

  figure.root.rotation.y = baseYaw;
  enableShadowCasting(figure.root);
  scene.add(figure.root);

  // ── Skybox hull presentation (tripod camera + raycast footing) ─────────
  // Active while `activeGround` is loaded. Tripod at (0,0,0) — capture point
  // in authored Skybox hull space (hull is no longer floor-snapped / flattened).
  const HULL_SKYBOX_FOV = 75;
  /** Face button: telephoto FOV + head aim (camera may still move with orbit). */
  const HULL_FACE_FOV = 20;
  /** Face orbit: ±90° azimuth from forward view (= 180° arc in front of character). */
  const FACE_AZIMUTH_HALF_ARC = Math.PI / 2;
  const PROFILE_FACE_FOV = 20;
  const hullTripodTarget = new Vector3();
  const profileCameraPos = new Vector3(0, 1.07, 3.506);
  const profileCameraTarget = new Vector3(0, 1.0, 0);
  const profileCameraFov = 32;
  let hullSkyboxMode = false;
  let camPreset: 'body' | 'face' = 'body';

  /** Head aim for face framing (hip world + CC-style head offset). */
  function getFaceLookAt(out: Vector3, scale = 1): Vector3 {
    figure.getHipWorldPosition(out);
    out.y += 0.62 * scale;
    return out;
  }

  function clearOrbitAzimuthLimits(): void {
    orbitControls.minAzimuthAngle = -Infinity;
    orbitControls.maxAzimuthAngle = Infinity;
  }

  function setFaceOrbitAzimuthLimits(): void {
    const center = orbitControls.getAzimuthalAngle();
    orbitControls.minAzimuthAngle = center - FACE_AZIMUTH_HALF_ARC;
    orbitControls.maxAzimuthAngle = center + FACE_AZIMUTH_HALF_ARC;
  }

  function applyProfileBodyDefault(): void {
    camPreset = 'body';
    camera.position.copy(profileCameraPos);
    target.copy(profileCameraTarget);
    camera.fov = profileCameraFov;
    camera.updateProjectionMatrix();
    camera.lookAt(target);
    orbitControls.target.copy(target);
    orbitControls.update();
    clearOrbitAzimuthLimits();
  }

  function resetProfileBodyCamera(): void {
    applyProfileBodyDefault();
    orbitControls.reset();
  }

  function applyProfileFaceCamera(): void {
    camPreset = 'face';
    getFaceLookAt(target);
    camera.fov = PROFILE_FACE_FOV;
    camera.updateProjectionMatrix();
    camera.lookAt(target);
    orbitControls.target.copy(target);
    orbitControls.update();
    setFaceOrbitAzimuthLimits();
  }

  /** Default hull body framing (same as first frame after hull load). */
  function applyHullBodyDefault(): void {
    camPreset = 'body';
    camera.position.set(0, 0, 0);
    hullTripodTarget.set(0, 0, -0.001);
    target.copy(hullTripodTarget);
    camera.fov = HULL_SKYBOX_FOV;
    camera.updateProjectionMatrix();
    camera.lookAt(hullTripodTarget);
    orbitControls.target.copy(hullTripodTarget);
    orbitControls.update();
    clearOrbitAzimuthLimits();
  }

  function resetHullBodyCamera(): void {
    applyHullBodyDefault();
    orbitControls.reset();
  }

  function applyHullFaceCamera(): void {
    camPreset = 'face';
    getFaceLookAt(hullTripodTarget, HULL_CHARACTER_SCALE);
    camera.fov = HULL_FACE_FOV;
    camera.updateProjectionMatrix();
    target.copy(hullTripodTarget);
    camera.lookAt(hullTripodTarget);
    orbitControls.target.copy(hullTripodTarget);
    orbitControls.update();
    setFaceOrbitAzimuthLimits();
  }

  function applyHullFigureTransform(): void {
    if (!activeGround) return;
    const z = hullCharacterWorldZ();
    const footY = hullCharacterFootY(activeGround, 0, z);
    figure.root.position.set(0, footY, z);
    figure.root.scale.setScalar(HULL_CHARACTER_SCALE);
    console.log(
      `[world] hull figure scale=${HULL_CHARACTER_SCALE} z=${z.toFixed(2)} footY=${footY.toFixed(3)}`,
    );
  }

  function syncHullShadowLight(): void {
    const p = figure.root.position;
    syncHullSunOverFeet(hullSun, p.x, p.y, p.z);
  }

  function enterHullSkyboxMode(): void {
    if (hullSkyboxMode) {
      applyHullFigureTransform();
      syncHullShadowLight();
      return;
    }
    hullSkyboxMode = true;
    key.castShadow = false;

    applyHullFigureTransform();
    syncHullShadowLight();
    applyHullBodyDefault();
    orbitControls.saveState();
    console.log('[world] hull Skybox presentation: tripod (0, 0, 0), fov 75°, hull shadow catchers');
  }

  function exitHullSkyboxMode(): void {
    if (!hullSkyboxMode) return;
    hullSkyboxMode = false;
    camPreset = 'body';
    key.castShadow = true;

    const prevBg = scene.background;
    if (prevBg && prevBg instanceof Texture) prevBg.dispose();
    scene.background = new Color(BACKGROUND);

    figure.root.position.set(0, 0, 0);
    figure.root.scale.setScalar(1);
    applyProfileBodyDefault();
    orbitControls.saveState();
    console.log('[world] hull Skybox presentation off — profile camera restored');
  }

  // ── Narration / lip sync ───────────────────────────────────────────────
  const narrationPlayer = new NarrationPlayer(DIRECTOR_URL);
  // Store the in-flight setup promise so multiple callers share it and
  // setup is never run twice concurrently.
  let narrationSetupPromise: Promise<void> | null = null;
  function ensureNarrationReady(): Promise<void> {
    if (!narrationSetupPromise) {
      narrationSetupPromise = narrationPlayer.setup(figure);
    }
    return narrationSetupPromise;
  }

  // ── Ambient facial baseline ────────────────────────────────────────────
  // Tiny always-on expression so the face never reads as a blank mannequin
  // between beats. Subtle brow furrow + microexpression weights — at the
  // amplitudes used here it reads as "the character is alive and thinking",
  // not as any specific emotion. The layered system in figure.ts ensures
  // a stronger beat emotion overrides this baseline cleanly.
  const AMBIENT_THINKING: Record<string, number> = {
    browInnerUp: 0.10,
    browOuterUpLeft: 0.04,
    browOuterUpRight: 0.04,
    eyeSquintLeft: 0.05,
    eyeSquintRight: 0.05,
    mouthPressLeft: 0.04,
    mouthPressRight: 0.04,
  };
  function applyAmbient(f: Figure): void {
    if (f.morphTargetNames.length === 0) return; // no blendshapes on this rig
    f.setAmbient(AMBIENT_THINKING);
  }
  applyAmbient(figure);

  // ── Animation manifest ──────────────────────────────────────────────────
  let manifest: ManifestEntry[] = [];
  try {
    const res = await fetch(ANIMS_MANIFEST_URL);
    if (res.ok) manifest = (await res.json()) as ManifestEntry[];
  } catch (err) {
    console.warn('[main] failed to load anims manifest', err);
  }
  // Exclude animations clearly authored for a female rig — the current
  // character is male and female-specific clips look wrong on it.
  manifest = manifest.filter((m) => !/female/i.test(m.name));

  // ── Breathing + default idle setup ─────────────────────────────────────
  const breathingEntry = manifest.find((m) => BREATHING_CLIP_MATCH.test(m.name));
  let currentBreathBpm = BREATHING_BPM_DEFAULT;
  let currentBreathAmp = BREATHING_AMP_DEFAULT;

  let defaultEntry: ManifestEntry | undefined;
  for (const re of DEFAULT_CLIP_MATCHERS) {
    defaultEntry = manifest.find((m) => re.test(m.name));
    if (defaultEntry) break;
  }
  defaultEntry ??= manifest[0];

  function applyBreathing(f: Figure): void {
    if (!breathingEntry) return;
    f.setBreathing({
      url: breathingEntry.url,
      bpm: currentBreathBpm,
      amplitude: currentBreathAmp,
    }).catch((err) => console.warn('[main] breathing layer failed', err));
  }
  applyBreathing(figure);

  // ── Beat player + director client ───────────────────────────────────────
  const beatPlayer = new BeatPlayer(
    figure,
    defaultEntry?.url ?? '',
    {
      // Refill triggers when the queue drops to this depth. With HY-Motion
      // in the pipeline, /direct/continue takes 15-25s (Gemini ~5s +
      // HY-Motion parallel ~10s + vocalization ~3s). At ~5s per beat avg,
      // threshold=8 gives ~40s of runway — enough to absorb the worst-case
      // refill round-trip without the queue ever hitting 0.
      queueLowThreshold: 8,
      onStateChange: (state) => updateDebugOverlay(state),
    },
  );

  // ── Idle pool ──────────────────────────────────────────────────────────
  // Use the same default-matched clip the rest of the system treats as
  // "idle" (Standing Idle Unarmed → Standing Idle → first /idle/ match).
  // Other Mixamo "idle" variants are too animated to read as a resting
  // baseline; the procedural-noise + facial ambient layers do the
  // "alive between beats" work instead.
  const idlePoolUrls = defaultEntry ? [defaultEntry.url] : [];
  beatPlayer.setIdlePool(idlePoolUrls);
  console.log(`[main] idle: ${defaultEntry?.name ?? '(none)'}`);

  // Set to true when the director transitions to 'playing'. Any buffered
  // ambient tracks are flushed at that point.
  let sceneReady = false;
  let pendingAmbient: { envUrl: string; musicUrl: string | null } | null = null;

  function syncAmbientButton(state: AmbientUiState): void {
    const btn = document.getElementById('ambient-btn') as HTMLButtonElement | null;
    if (!btn) return;
    btn.classList.toggle('ambient-visible', ambientPlayer.isAmbientSessionActive());
    const iconOn = btn.querySelector('.ambient-icon-on') as HTMLElement | null;
    const iconOff = btn.querySelector('.ambient-icon-off') as HTMLElement | null;
    btn.classList.remove('ambient-on', 'ambient-loading', 'ambient-error');
    const showMutedIcon = state === 'off';
    if (showMutedIcon) {
      iconOn?.setAttribute('hidden', '');
      iconOff?.removeAttribute('hidden');
    } else {
      iconOff?.setAttribute('hidden', '');
      iconOn?.removeAttribute('hidden');
    }
    switch (state) {
      case 'off':
        btn.title = 'Turn on ambient sound';
        break;
      case 'waiting':
        btn.classList.add('ambient-on', 'ambient-loading');
        btn.title = 'Ambient starting…';
        break;
      case 'loading':
        btn.classList.add('ambient-on', 'ambient-loading');
        btn.title = 'Loading ambient…';
        break;
      case 'playing':
        btn.classList.add('ambient-on');
        btn.title = 'Turn off ambient sound';
        break;
      case 'error':
        btn.classList.add('ambient-on', 'ambient-error');
        btn.title = 'Ambient failed — tap to retry';
        break;
    }
  }

  const ambientPlayer = new AmbientPlayer({ onStateChange: syncAmbientButton });

  const director = new DirectorClient({
    serverUrl: DIRECTOR_URL,
    beatPlayer,
    onStateChange: (state) => handleDirectorState(state),
    onStreamConnected: () => setLoadingStep(2),
    onVocalization: (url, offsetSec) => {
      void ensureNarrationReady().then(() => narrationPlayer.speakFromUrl(url, offsetSec));
    },
    onAmbient: (envUrl, musicUrl) => {
      // Buffer the URLs — only start playing once the scene is fully loaded
      // (loading overlay dismissed, director in 'playing' state).
      pendingAmbient = { envUrl, musicUrl };
      if (sceneReady) ambientPlayer.play(envUrl, musicUrl);
    },
    onWorldJob: (operationId, prompt) => {
      // Light up the "Building environment" step in the unified loading
      // panel; previously this also un-hid #world-env-badge, which has
      // been folded into the prompt-card list (Task 4).
      startEnvLoad();
      worldLoader.start(operationId, prompt);
    },
  });

  // ── World environment loader ─────────────────────────────────────────────
  const worldLoader = new WorldLoader(DIRECTOR_URL);

  worldLoader.onProgress = (desc) => {
    // Pipe granular progress strings ("Generating panorama…",
    // "Exporting mesh…") into the env step's label so the user sees the
    // sub-stage rather than just a static "Building environment".
    setEnvProgressLabel(desc);
  };

  // Prompt-side gate. The panorama Stage 1 preview shows the LLM-generated
  // skybox as a GroundedSkybox dome BEFORE the textured 3D hull (Stage 2)
  // arrives. Setting this to false suppresses just that preview during
  // prompt-initiated world generation; the hull still loads normally and
  // (when present) re-applies the panorama as an equirectangular backdrop
  // inside the hull. The cache-dropdown path (loadCachedEntryFromOption)
  // is unaffected — pano-only cached entries still display via
  // loadPanoramaBackground because they have no hull to fall back to.
  const SHOW_PANO_BEFORE_HULL = false;

  // Skybox AI Stage 1 — panorama appears as scene background immediately so
  // the user sees the environment within ~20s rather than waiting the full
  // ~60-90s for the GLB mesh (when available) to finish building.
  worldLoader.onSkyboxPanoReady = (pano) => {
    if (!SHOW_PANO_BEFORE_HULL) return;
    void loadPanoramaBackground(pano.panoUrl);
  };

  // Terminal callback for the Skybox path. mesh is null on pano-only
  // completions (e.g. when the API tier doesn't permit GLB export). When
  // SHOW_PANO_BEFORE_HULL is false and we got a mesh, the pano was never
  // loaded — loadGroundMesh will install it as the hull's equirect backdrop.
  // When SHOW_PANO_BEFORE_HULL is false and there is no mesh, we fall back
  // to the panorama dome anyway (better than a blank scene).
  worldLoader.onSkyboxComplete = (pano, mesh) => {
    if (mesh) {
      void loadGroundMesh(mesh.glbUrl, pano.panoUrl);
    } else if (!SHOW_PANO_BEFORE_HULL) {
      void loadPanoramaBackground(pano.panoUrl);
    }
    completeEnvLoad();
  };

  // WorldLabs (legacy) — single splat asset, fires once when ready.
  worldLoader.onWorldLabsReady = (assets) => {
    loadSplat(assets.splatUrl500k, /* openCvCorrection */ true);
    completeEnvLoad();
  };

  worldLoader.onCached = (entry) => {
    // Just-cached world arrived in the server cache — add it to the dropdown
    // so it can be replayed in future sessions.
    if (!splatSelectEl) return;
    // Avoid duplicate entries (e.g. if onCached fires twice for the same id).
    for (const opt of Array.from(splatSelectEl.options)) {
      if (opt.dataset['id'] === entry.id) return;
    }
    const opt = document.createElement('option');
    opt.value = entry.id;
    opt.dataset['id'] = entry.id;
    if (entry.splatLocalUrl) opt.dataset['splatUrl'] = entry.splatLocalUrl;
    if (entry.panoLocalUrl) opt.dataset['panoUrl'] = entry.panoLocalUrl;
    if (entry.glbLocalUrl) opt.dataset['glbUrl'] = entry.glbLocalUrl;
    opt.dataset['source'] = entry.panoLocalUrl ? 'blockade' : 'worldlabs';
    const date = new Date().toLocaleDateString();
    const tag = entry.glbLocalUrl
      ? '[3D]'
      : entry.panoLocalUrl
        ? '[Pano]'
        : '[Splat]';
    opt.textContent = `${tag} ${date} — ${entry.prompt.slice(0, 50)}`;
    splatSelectEl.appendChild(opt);
    // Do not auto-select — that made the next prompt send skip_world_gen and
    // Blockade never received a new /skybox request.
    console.log('[world] cached entry added to dropdown:', entry.id);
  };

  worldLoader.onError = () => {
    // Mark the env step done on failure too — we don't want the unified
    // card stuck open waiting for an environment that will never arrive.
    completeEnvLoad();
  };

  // ── UI element refs ──────────────────────────────────────────────────────
  const promptOverlayEl = document.getElementById('prompt-overlay');
  const promptCardEl = document.getElementById('prompt-card');
  const promptFormEl = document.getElementById('prompt-form') as HTMLFormElement | null;
  const promptTextareaEl = document.getElementById('prompt-input') as HTMLTextAreaElement | null;
  const promptSubmitEl = document.getElementById('prompt-submit') as HTMLButtonElement | null;
  const promptErrorEl = document.getElementById('prompt-error');
  const promptCloseEl = document.getElementById('prompt-close') as HTMLButtonElement | null;
  const promptModeToggleEl = document.getElementById('prompt-mode-toggle') as HTMLDivElement | null;
  const promptModeSegmentEls = promptModeToggleEl?.querySelectorAll<HTMLButtonElement>('.seg') ?? null;

  // ── Bias mode toggle (segmented control) ─────────────────────────────────
  // Story (default): prompt is the first chapter of an unfolding narrative.
  // Scene: prompt is an ongoing activity the character keeps doing.
  // The toggle's value is captured at submit time and locked for the
  // session — flipping mid-session does NOT alter the running performance;
  // a fresh prompt is required to apply a different mode. Persisted to
  // localStorage so a returning user sees their last choice.
  const BIAS_MODE_STORAGE_KEY = 'biasMode';
  let biasMode: 'story' | 'scene' =
    localStorage.getItem(BIAS_MODE_STORAGE_KEY) === 'scene' ? 'scene' : 'story';
  function syncBiasModeToggle(): void {
    if (!promptModeSegmentEls) return;
    promptModeSegmentEls.forEach((seg) => {
      const isActive = seg.dataset['mode'] === biasMode;
      seg.setAttribute('aria-checked', String(isActive));
    });
  }
  syncBiasModeToggle();
  // Card starts hidden in HTML so the hardcoded Story default on the toggle
  // doesn't flash before localStorage is applied. Reveal once sync runs.
  promptCardEl?.classList.remove('hidden');
  promptModeSegmentEls?.forEach((seg) => {
    seg.addEventListener('click', () => {
      const mode = seg.dataset['mode'];
      if (mode !== 'story' && mode !== 'scene') return;
      if (mode === biasMode) return;
      biasMode = mode;
      localStorage.setItem(BIAS_MODE_STORAGE_KEY, biasMode);
      syncBiasModeToggle();
    });
  });

  // ── Loading status pills (bottom-centre) ──────────────────────────────
  // Single source of truth for "what's loading right now". A small Set of
  // active step keys + a fixed priority list — the main pill renders the
  // first step in priority order that's currently active, or hides if
  // nothing is. The cold-start pill is independent (driven directly by
  // waitForAnimationServer) and explains why the user is staring at
  // "Generating animations" for so long when the GPU is warming.
  //
  // Priority rationale (per UX intent — what the user is mentally tracking):
  //   plan      — "the system is making my story" (highest)
  //   server    — "Generating animations" (visible during cold-start)
  //   narration — preparing TTS / lipsync
  //   env       — building the 3D world (slowest, lowest user concern)
  //   connect   — initial fetch handshake; almost-always invisible because
  //               server/plan supersede it. Acts as a fallback label for
  //               the brief moment before /hy-motion/health responds.
  type LoadStep = 'plan' | 'server' | 'narration' | 'env' | 'connect';
  const LOAD_PRIORITY: LoadStep[] = ['plan', 'server', 'narration', 'env', 'connect'];
  const LOAD_LABELS: Record<Exclude<LoadStep, 'env'>, string> = {
    plan: 'Planning your scene',
    server: 'Generating animations',
    narration: 'Preparing narration',
    connect: 'Connecting to server',
  };

  // Each pill text must remain on screen at least this long, so flashes like
  // narration (which resolves in ~50ms because TTS is per-beat) are still
  // legible. Implemented via a deferred refresh in refreshLoadingPill: when
  // a step transition is requested before the current step has met its
  // minimum, the change is delayed with a setTimeout instead of applied.
  const MIN_STEP_VISIBLE_MS = 800;

  const activeLoads = new Set<LoadStep>();
  let envProgressLabel = 'Building environment';

  // Sequential phase = everything except env. While at least one sequential
  // step is *expected* to still fire, we suppress env in the pill display
  // AND keep #prompt-overlay in its solid is-loading state so the 3D
  // scene / panorama can't peek through. Flips to true once the director
  // hits 'playing' AND ensureNarrationReady() resolves with min-visibility
  // applied — at which point env surfaces in the pill (if still loading)
  // and the overlay drops, revealing the scene.
  let sequentialDone = false;

  const loadingPillEl = document.getElementById('loading-pill');
  const loadingPillLabelEl = loadingPillEl?.querySelector('.loading-pill-label') as HTMLElement | null;
  const coldStartPillEl = document.getElementById('coldstart-pill');

  // Track what's currently *displayed* (vs what's currently active in
  // activeLoads) so min-visibility can defer transitions without losing
  // the underlying state.
  let displayedStep: LoadStep | null = null;
  let displayedAt = 0;
  let pendingPillTimer: number | null = null;

  function computeTargetStep(): LoadStep | null {
    for (const step of LOAD_PRIORITY) {
      if (!activeLoads.has(step)) continue;
      // Suppress env until the sequential phase has fully finished. Without
      // this gate, narration's near-instant resolve lets env take over the
      // pill before the user has even read "Preparing narration".
      if (step === 'env' && !sequentialDone) continue;
      return step;
    }
    return null;
  }

  function refreshLoadingPill(): void {
    const target = computeTargetStep();

    // Live-update env progress label without re-running the transition logic.
    if (displayedStep === 'env' && target === 'env') {
      if (loadingPillLabelEl) loadingPillLabelEl.textContent = envProgressLabel;
      return;
    }

    if (target === displayedStep) return;

    // Min-visibility gate: if the current step hasn't been on screen long
    // enough, defer the transition. setEnvProgressLabel / setColdStart
    // calls during the deferral keep working because they don't go through
    // this path.
    if (displayedStep !== null) {
      const elapsed = performance.now() - displayedAt;
      if (elapsed < MIN_STEP_VISIBLE_MS) {
        if (pendingPillTimer !== null) clearTimeout(pendingPillTimer);
        pendingPillTimer = window.setTimeout(() => {
          pendingPillTimer = null;
          refreshLoadingPill();
        }, MIN_STEP_VISIBLE_MS - elapsed);
        return;
      }
    }

    displayedStep = target;
    displayedAt = performance.now();
    if (!loadingPillEl || !loadingPillLabelEl) return;
    if (target === null) {
      loadingPillEl.classList.add('hidden');
    } else {
      loadingPillEl.classList.remove('hidden');
      loadingPillLabelEl.textContent =
        target === 'env' ? envProgressLabel : LOAD_LABELS[target];
    }
  }

  function setLoading(step: LoadStep, active: boolean): void {
    if (active) activeLoads.add(step);
    else activeLoads.delete(step);
    refreshLoadingPill();
  }

  function clearAllLoading(): void {
    activeLoads.clear();
    envProgressLabel = 'Building environment';
    sequentialDone = false;
    if (pendingPillTimer !== null) {
      clearTimeout(pendingPillTimer);
      pendingPillTimer = null;
    }
    // Force the pill hidden immediately — clearAllLoading is called from
    // abort / error / Home, and we don't want a stale step lingering due
    // to min-visibility while the user is back at the form.
    displayedStep = null;
    displayedAt = 0;
    loadingPillEl?.classList.add('hidden');
  }

  function setColdStart(active: boolean): void {
    coldStartPillEl?.classList.toggle('hidden', !active);
  }

  /**
   * Mark the sequential phase done so env is allowed to surface in the
   * pill. Honours min-visibility on whatever sequential step is currently
   * on screen so flashes (e.g. narration) stay readable before the pill
   * rolls over to env. Does NOT touch the overlay or the scene — those
   * are handled independently by the submit / error / Home flows.
   */
  function finishSequentialPhase(): void {
    const flip = (): void => {
      sequentialDone = true;
      refreshLoadingPill();
    };
    if (displayedStep !== null) {
      const elapsed = performance.now() - displayedAt;
      const remaining = MIN_STEP_VISIBLE_MS - elapsed;
      if (remaining > 0) {
        window.setTimeout(flip, remaining);
        return;
      }
    }
    flip();
  }

  // setLoadingStep(1|2|3) is the legacy entry point used by the submit
  // handler and the SSE 'playing' / stream-connected callbacks. It maps
  // each numeric step to its corresponding pill state and clears the
  // others in the sequential family (connect/plan/narration). server is
  // managed independently by waitForAnimationServer; env by the world-
  // loader callbacks. Mapping:
  //   step 1 → connect (set on submit, cleared once stream connects)
  //   step 2 → plan    (set on stream connected)
  //   step 3 → narration (set on 'playing', cleared on ensureNarrationReady)
  function setLoadingStep(active: 1 | 2 | 3): void {
    activeLoads.delete('connect');
    activeLoads.delete('plan');
    activeLoads.delete('narration');
    if (active === 1) activeLoads.add('connect');
    else if (active === 2) activeLoads.add('plan');
    else if (active === 3) activeLoads.add('narration');
    refreshLoadingPill();
  }

  // ── Environment-load tracking ────────────────────────────────────────
  // Now feeds the unified pill instead of an inline step row. The card
  // no longer waits for env to finish before dismissing — the user sees
  // the env pill at the bottom while the world generates.
  function startEnvLoad(): void {
    setLoading('env', true);
  }

  function completeEnvLoad(): void {
    setLoading('env', false);
  }

  function setEnvProgressLabel(text: string): void {
    envProgressLabel = text;
    refreshLoadingPill();
  }

  function resetEnvLoad(): void {
    activeLoads.delete('env');
    envProgressLabel = 'Building environment';
    refreshLoadingPill();
  }

  /**
   * Poll the director server's /hy-motion/health proxy until the GPU service
   * is ready. Returns true when ready, false if it times out or is unconfigured.
   *
   * Shows "Starting animation server" step as soon as the service is found to
   * be warming up. The scene must NOT start until this resolves with true.
   */
  type ServerCheckResult = 'ready' | 'error' | 'timeout' | 'unreachable';

  async function waitForAnimationServer(): Promise<ServerCheckResult> {
    let data: { status?: string } = {};
    try {
      const res = await fetch(`${DIRECTOR_URL}/hy-motion/health`);
      data = await res.json() as { status?: string };
    } catch {
      return 'unreachable';
    }

    if (data.status === 'not_configured') return 'ready';
    if (data.status === 'ok' || data.status === 'ready') return 'ready';
    if (data.status === 'error') return 'error';

    // GPU is warming — surface both pills:
    //   - main: "Generating animations" (server step active)
    //   - secondary: "Animation Server: cold-starting"
    setLoading('server', true);
    setColdStart(true);

    const deadline = Date.now() + 10 * 60_000; // 10 min — Qwen3-8B download can take ~5 min
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 4000));
      try {
        const r2 = await fetch(`${DIRECTOR_URL}/hy-motion/health`);
        const d2 = await r2.json() as { status?: string };
        if (d2.status === 'ok' || d2.status === 'ready') {
          setLoading('server', false);
          setColdStart(false);
          return 'ready';
        }
        if (d2.status === 'error') {
          setLoading('server', false);
          setColdStart(false);
          return 'error';
        }
      } catch { /* retry on network error */ }
    }

    setLoading('server', false);
    setColdStart(false);
    return 'timeout';
  }

  /** Reset the prompt form after a failed start attempt. */
  function abortLoading(message: string): void {
    // Wipe every pill state so the next attempt starts clean. The cold-
    // start pill is independent of the priority Set, so toggle it
    // explicitly.
    clearAllLoading();
    setColdStart(false);
    promptOverlayEl?.classList.remove('hidden');
    if (promptErrorEl) {
      promptErrorEl.textContent = message;
      promptErrorEl.classList.remove('hidden');
    }
    if (promptSubmitEl) {
      promptSubmitEl.disabled = false;
      promptSubmitEl.textContent = 'Begin';
    }
  }

  const repromptBtnEl = document.getElementById('reprompt-btn') as HTMLButtonElement | null;
  const tuneBtnEl = document.getElementById('tune-btn') as HTMLButtonElement | null;
  const directBtnEl = document.getElementById('direct-btn') as HTMLButtonElement | null;
  const cacheBtnEl = document.getElementById('cache-btn') as HTMLButtonElement | null;
  const ambientBtnEl = document.getElementById('ambient-btn') as HTMLButtonElement | null;
  const meshBtnEl = document.getElementById('mesh-btn') as HTMLButtonElement | null;

  // ── Mesh-only render mode (toggle next to the ambient button) ──────────
  // Swaps every mesh under a given root for a Blender-style "solid +
  // wireframe" look:
  //   - Original mesh repainted with a uniform shaded grey fill (with a
  //     tiny polygonOffset to recede behind the overlay).
  //   - A sibling clone with a dark wireframe material is parented under
  //     the original. SkinnedMesh siblings re-bind to the source skeleton
  //     so animation deforms both meshes in lockstep — same trick used by
  //     figure.ts for the legacy "Body Field" wire overlay.
  // State (originals + sibling refs) lives in WeakMaps keyed by the
  // original mesh, so scene-graph teardown (figure swap, ground replace)
  // garbage-collects entries automatically.
  //
  // Applied to BOTH the figure (always) and the depth-displaced 3D mesh
  // hull from Skybox AI (`activeGround`, only when present). The pano
  // dome (GroundedSkybox `activeSkybox`) is intentionally untouched — the
  // user explicitly scoped this to the 3D mesh hull. When the GLB hull
  // is loaded, the dome has already been cleared by `clearSkybox()`
  // inside `loadGroundMesh`, so the two never coexist.
  let meshOnlyMode = false;
  const originalMeshMaterials = new WeakMap<Mesh, Material | Material[]>();
  const meshOnlyWireSiblings = new WeakMap<Mesh, Mesh>();
  const meshOnlyMaterial = new MeshStandardMaterial({
    color: 0xb5b5b5,
    roughness: 0.85,
    metalness: 0.0,
    // Push solid fragments slightly into the depth buffer so the wire
    // overlay below doesn't z-fight at glancing angles.
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 1,
  });
  const meshOnlyWireMaterial = new MeshBasicMaterial({
    color: 0x2a2a2a,
    wireframe: true,
    transparent: true,
    opacity: 0.7,
  });

  function applyMeshOnlyToObject(root: Object3D, enable: boolean): void {
    // Snapshot the mesh list before mutating — adding wire siblings during
    // a live traverse would re-process them as new meshes (despite the
    // userData guard, snapshotting is the more defensive pattern).
    const meshes: Mesh[] = [];
    root.traverse((node) => {
      const m = node as Mesh;
      if (!m.isMesh) return;
      // Skip our own wire siblings — they're recognisable by the userData
      // flag and shouldn't be processed as primary meshes.
      if (m.userData['isMeshOnlyWire']) return;
      meshes.push(m);
    });

    for (const mesh of meshes) {
      if (enable) {
        // First-activation cache only — re-toggling must not capture the
        // grey override as the "original".
        if (!originalMeshMaterials.has(mesh)) {
          originalMeshMaterials.set(mesh, mesh.material);
        }
        mesh.material = meshOnlyMaterial;

        // Add the wireframe sibling on first enable for this mesh. If
        // one already exists (re-enable after disable+enable on the same
        // root), don't double-add.
        if (!meshOnlyWireSiblings.has(mesh)) {
          const wire = mesh.clone() as Mesh;
          wire.material = meshOnlyWireMaterial;
          wire.userData['isMeshOnlyWire'] = true;
          wire.castShadow = false;
          wire.receiveShadow = false;
          // Skinned figure bounding boxes occasionally report empty
          // mid-pose, which would frustum-cull the wires off-screen.
          wire.frustumCulled = false;
          wire.renderOrder = (mesh.renderOrder ?? 0) + 1;
          // Re-bind the skeleton so the wire deforms with the same
          // animation as the source. Plain Mesh clones don't need this.
          if ((mesh as SkinnedMesh).isSkinnedMesh) {
            const sm = mesh as SkinnedMesh;
            (wire as SkinnedMesh).bind(sm.skeleton, sm.bindMatrix);
          }
          mesh.parent?.add(wire);
          meshOnlyWireSiblings.set(mesh, wire);
        }
      } else {
        const orig = originalMeshMaterials.get(mesh);
        if (orig) mesh.material = orig;

        // Remove the wireframe sibling from the scene graph. WeakMap
        // entries clear themselves on next GC; explicit delete keeps the
        // current invariant clean.
        const wire = meshOnlyWireSiblings.get(mesh);
        if (wire) {
          wire.parent?.remove(wire);
          meshOnlyWireSiblings.delete(mesh);
        }
      }
    }
  }

  /**
   * Apply the current mesh-only state to every targetable root in the
   * scene. The figure is always processed; the 3D mesh hull is only
   * processed when it has finished loading (`activeGround` non-null) —
   * matches the user's "only if the 3D mesh has finished loading"
   * requirement. Used both by the toggle and by `loadGroundMesh` to
   * retroactively style a hull that arrives after the user has already
   * enabled the toggle.
   */
  function applyMeshOnlyToScene(enable: boolean): void {
    applyMeshOnlyToObject(figure.root, enable);
    if (activeGround) applyMeshOnlyToObject(activeGround, enable);
  }

  function reapplyMeshOnlyForCurrentFigure(): void {
    if (meshOnlyMode) applyMeshOnlyToObject(figure.root, true);
  }

  function reapplyMeshOnlyForCurrentGround(): void {
    if (meshOnlyMode && activeGround) applyMeshOnlyToObject(activeGround, true);
  }

  if (meshBtnEl) {
    meshBtnEl.addEventListener('click', () => {
      meshOnlyMode = !meshOnlyMode;
      meshBtnEl.classList.toggle('mesh-on', meshOnlyMode);
      meshBtnEl.textContent = meshOnlyMode ? 'Textured' : 'Mesh';
      applyMeshOnlyToScene(meshOnlyMode);
    });
  }
  const cacheBrowserRootEl = document.getElementById('cache-browser');
  const narratePanelEl = document.getElementById('narrate-panel');
  const narrateFormEl = document.getElementById('narrate-form') as HTMLFormElement | null;
  const narrateInputEl = document.getElementById('narrate-input') as HTMLInputElement | null;

  narrateFormEl?.addEventListener('submit', (e) => {
    e.preventDefault();
    const text = narrateInputEl?.value.trim() ?? '';
    if (!text) return;
    void narrationPlayer.speak(text, currentVoiceId);
    if (narrateInputEl) narrateInputEl.value = '';
  });

  // Cache browser — bottom-left panel for inspecting/pruning the Uthana cache.
  // Uses a figureRef closure (not a captured value) so previews always hit
  // the *current* figure even after a character swap. Hidden by default;
  // opened via the Cache button (bottom-right) or the C hotkey.
  const cacheBrowser = cacheBrowserRootEl
    ? new CacheBrowser({
        serverUrl: DIRECTOR_URL,
        figureRef: () => figure,
        rootEl: cacheBrowserRootEl,
      })
    : null;
  // Initial fetch — populates the list silently in the background. The
  // panel stays hidden (userHidden defaults to true) until the user
  // explicitly opens it.
  void cacheBrowser?.refresh();
  cacheBtnEl?.addEventListener('click', () => cacheBrowser?.toggle());

  // Ambient sound toggle — state driven by AmbientPlayer.getUiState().
  ambientBtnEl?.addEventListener('click', () => {
    ambientPlayer.ensureUnlocked();
    const state = ambientPlayer.getUiState();
    if (state === 'off' || state === 'error') {
      ambientPlayer.retry();
    } else {
      ambientPlayer.pause();
    }
  });
  ambientPlayer.refreshUi();

  // ── Tuning panel ────────────────────────────────────────────────────────
  // Live calibration tool for procedural hold poses + body-state biases.
  // Mounts itself into <body> as a side panel; toggled via the "Tune" button
  // or the T key. Stops the BeatPlayer while open so beats don't compete
  // with what's being calibrated.
  let tuningPanel = createTuningPanel({
    figure,
    beatPlayer,
    idleClipUrl: defaultEntry?.url,
    serverUrl: DIRECTOR_URL,
    onSplatTransformChange,
  });
  if (tuneBtnEl) {
    tuneBtnEl.addEventListener('click', () => {
      tuningPanel.toggle();
      tuneBtnEl.textContent = tuningPanel.isOpen ? 'Close tune' : 'Tune';
    });
  }

  // ── Scene Director panel ──────────────────────────────────────────────
  // Hand-authored beat sequence editor. Calls director.stop() on play
  // so an LLM stream and a manual scene can't fight each other on the
  // same BeatPlayer. The 'M' key toggles open/close (D is taken by
  // the debug overlay).
  let sceneDirectorPanel = createSceneDirectorPanel({
    figure,
    beatPlayer,
    director,
    serverUrl: DIRECTOR_URL,
    idleClipUrl: defaultEntry?.url,
  });
  if (directBtnEl) {
    directBtnEl.addEventListener('click', () => {
      sceneDirectorPanel.toggle();
      directBtnEl.textContent = sceneDirectorPanel.isOpen ? 'Close direct' : 'Direct';
    });
  }

  // ── Character selector ────────────────────────────────────────────────────
  // Dropdown (bottom-right dev cluster) that lets users hot-swap the avatar.
  // The full character list comes from characters.json which was already
  // fetched above into `charactersManifest`.
  let currentCharacterUrl = initialCharacterUrl;
  let currentVoiceId: string | undefined =
    charactersManifest.find((m) => m.url === initialCharacterUrl)?.voiceId;
  let currentGender: string =
    charactersManifest.find((m) => m.url === initialCharacterUrl)?.gender ?? 'male';
  const charSelectEl = document.getElementById('char-select') as HTMLSelectElement | null;

  if (charSelectEl && charactersManifest.length > 0) {
    // Populate options from manifest. Entries marked `hidden: true` are
    // skipped here but still loadable via VITE_CHARACTER_URL — handy for
    // staging a character (rig/morph fixes pending) without exposing it
    // to end users.
    for (const entry of charactersManifest) {
      if (entry.hidden) continue;
      const opt = document.createElement('option');
      opt.value = entry.url;
      opt.textContent = entry.skeletonFamily
        ? `${entry.name} (${entry.skeletonFamily})`
        : entry.name;
      if (entry.url === currentCharacterUrl) opt.selected = true;
      charSelectEl.appendChild(opt);
    }

    charSelectEl.addEventListener('change', () => {
      void switchCharacter(charSelectEl.value);
    });
  }

  async function switchCharacter(url: string): Promise<void> {
    if (url === currentCharacterUrl) return;
    if (charSelectEl) charSelectEl.disabled = true;

    // Halt any active LLM session, ambient audio, world loading, and beat queue
    director.stop();
    ambientPlayer.stop();
    worldLoader.stop();
    clearWorld();
    resetEnvLoad();
    beatPlayer.stop();

    // Close panels before teardown so their cached figure refs can't fire
    tuningPanel.close();
    sceneDirectorPanel.close();

    // Remove the old figure from the scene and release all GPU/CPU resources
    figure.teardown();
    scene.remove(figure.root);

    try {
      figure = await loadFigure(url, loadOptsFor(url));
    } catch (err) {
      console.error('[main] character load failed', err);
      if (charSelectEl) {
        charSelectEl.disabled = false;
        charSelectEl.value = currentCharacterUrl;
      }
      return;
    }

    figure.root.rotation.y = baseYaw;
    enableShadowCasting(figure.root);
    scene.add(figure.root);
    if (hullSkyboxMode) applyHullFigureTransform();
    win['__figure'] = figure;

    // Rebind narration lip sync to the new character's morph targets.
    if (narrationSetupPromise) {
      narrationSetupPromise = narrationPlayer.setup(figure);
    }

    // Re-apply always-on layers for the new rig
    applyAmbient(figure);
    applyBreathing(figure);
    // Carry the mesh-only state forward across character swaps so the user
    // doesn't see a flash of textures when picking a new character while
    // the toggle is on.
    reapplyMeshOnlyForCurrentFigure();

    // Point the beat player at the new figure and restart the idle loop
    beatPlayer.setFigure(figure);
    beatPlayer.start();

    // Rebuild dev panels — they close over the figure reference so they must
    // be recreated; the event listeners in main close over the `let` variables
    // so they automatically target the new panel instances after reassignment.
    tuningPanel.destroy();
    tuningPanel = createTuningPanel({
      figure,
      beatPlayer,
      idleClipUrl: defaultEntry?.url,
      serverUrl: DIRECTOR_URL,
      onSplatTransformChange,
    });

    sceneDirectorPanel.destroy();
    sceneDirectorPanel = createSceneDirectorPanel({
      figure,
      beatPlayer,
      director,
      serverUrl: DIRECTOR_URL,
      idleClipUrl: defaultEntry?.url,
    });

    currentCharacterUrl = url;
    currentVoiceId = charactersManifest.find((m) => m.url === url)?.voiceId;
    currentGender = charactersManifest.find((m) => m.url === url)?.gender ?? 'male';
    if (charSelectEl) charSelectEl.disabled = false;
    console.log(`[main] switched character → ${url}`);
  }

  const debugOverlayEl = document.getElementById('debug-overlay');
  const whyPillEl = document.getElementById('why-pill');
  const debugPromptEl = document.getElementById('debug-prompt');
  const debugBeatEl = document.getElementById('debug-beat');
  const debugDurationEl = document.getElementById('debug-duration');
  const debugReasonEl = document.getElementById('debug-reason');
  const debugClipReasonEl = document.getElementById('debug-clip-reason');
  const debugClipMatchEl = document.getElementById('debug-clip-match');
  const debugSourceEl = document.getElementById('debug-source');
  const debugEmotionEl = document.getElementById('debug-emotion');
  const debugEnergyEl = document.getElementById('debug-energy');
  const debugGazeEl = document.getElementById('debug-gaze');
  const debugGestureEl = document.getElementById('debug-gesture');
  const debugBodyGestureEl = document.getElementById('debug-body-gesture');
  const debugBodyStateEl = document.getElementById('debug-body-state');
  const debugHoldsEl = document.getElementById('debug-holds');
  const debugPoseEl = document.getElementById('debug-pose');
  const debugFadeEl = document.getElementById('debug-fade');
  const debugMirroredEl = document.getElementById('debug-mirrored');
  const debugHeartEl = document.getElementById('debug-heart');
  const debugHeartPulseEl = document.getElementById('debug-heart-pulse');
  const debugBreathEl = document.getElementById('debug-breath');
  const debugTimescaleEl = document.getElementById('debug-timescale');
  const debugQueueEl = document.getElementById('debug-queue');
  const debugElapsedEl = document.getElementById('debug-elapsed');
  const debugBeatsEl = document.getElementById('debug-beats');
  const debugPaidEl = document.getElementById('debug-paid');
  const debugRefillEl = document.getElementById('debug-refill');

  const breathHudEl = document.getElementById('breath-hud');

  // ── Director state → UI ─────────────────────────────────────────────────
  // The "Home" (re-prompt) button is hidden via CSS whenever #prompt-overlay
  // is visible (general-sibling rule in index.html), so director state does
  // not need to toggle it directly — show/hide of #prompt-overlay drives it.

  // Centralised debug-visibility setter: keeps debug overlay and why-pill
  // in sync — pill is visible exactly when debug overlay is hidden.
  function setDebugVisible(visible: boolean): void {
    debugVisible = visible;
    debugOverlayEl?.classList.toggle('hidden', !visible);
    whyPillEl?.classList.toggle('hidden', visible);
  }

  function handleDirectorState(state: DirectorState): void {
    if (state.type === 'loading') {
      sceneReady = false;
      pendingAmbient = null;
      narratePanelEl?.classList.add('hidden');
      // The pill is already showing "Connecting to server" from the
      // submit handler. No further action here.
    } else if (state.type === 'playing') {
      sceneReady = true;
      narratePanelEl?.classList.remove('hidden');
      // Sequential step → narration. setLoadingStep(3) clears any prior
      // sequential step (connect/plan/server) and marks narration active.
      setLoadingStep(3);
      // Flush any ambient tracks that arrived before the scene was ready.
      if (pendingAmbient) {
        ambientPlayer.play(pendingAmbient.envUrl, pendingAmbient.musicUrl);
        pendingAmbient = null;
      }
      // When narration init resolves: clear the sequential step, then
      // finishSequentialPhase() honours min-visibility before dropping
      // the overlay and unsuppressing env. ensureNarrationReady is fast
      // (per-beat TTS now), so without min-visibility narration would
      // flash invisibly.
      void ensureNarrationReady().finally(() => {
        setLoading('narration', false);
        finishSequentialPhase();
      });
      // Force-populate with the current beat player state — beats may have
      // already fired before this handler ran.
      updateDebugOverlay(beatPlayer.state);
    } else if (state.type === 'error') {
      // Wipe pill state and bring the form back with the error pinned.
      clearAllLoading();
      setColdStart(false);
      promptOverlayEl?.classList.remove('hidden');
      if (promptErrorEl) {
        promptErrorEl.textContent = state.message;
        promptErrorEl.classList.remove('hidden');
      }
      if (promptSubmitEl) {
        promptSubmitEl.disabled = false;
        promptSubmitEl.textContent = 'Begin';
      }
    }
  }

  // ── World cache dropdown ────────────────────────────────────────────────
  // Lists every cached environment (WorldLabs splats, Blockade pano+mesh
  // pairs, and any imported files) with format-tagged labels. The selected
  // option's dataset attributes carry whichever asset URLs apply to that
  // entry, so the change handler can dispatch on entry kind without
  // re-querying the server.
  const splatSelectEl = document.getElementById('splat-select') as HTMLSelectElement | null;
  void (async () => {
    try {
      const wgRes = await fetch(`${DIRECTOR_URL}/world-gen/status`);
      if (wgRes.ok) {
        const wg = (await wgRes.json()) as {
          provider: string;
          blockadeConfigured: boolean;
          canGenerate: boolean;
        };
        console.log('[world-gen] server config:', wg);
        if (!wg.canGenerate) {
          console.warn(
            '[world-gen] this director cannot generate environments — ' +
              'set BLOCKADE_LABS_API_KEY (and run server with npm run dev or npm start after build)',
          );
        }
      }
    } catch {
      console.warn('[world-gen] could not reach director /world-gen/status');
    }
  })();

  void (async () => {
    if (!splatSelectEl) return;
    splatSelectEl.disabled = false;
    try {
      const res = await fetch(`${DIRECTOR_URL}/world-cache`);
      if (!res.ok) {
        console.warn(`[world-cache] GET /world-cache returned ${res.status}`);
        return;
      }
      const data = (await res.json()) as {
        entries: Array<{
          id: string;
          prompt: string;
          createdAt: string;
          source: 'worldlabs' | 'blockade' | 'imported';
          splatLocalUrl?: string;
          panoLocalUrl?: string;
          glbLocalUrl?: string;
        }>;
      };
      console.log(`[world-cache] loaded ${data.entries.length} cached entries`);
      for (const entry of data.entries) {
        const opt = document.createElement('option');
        opt.value = entry.id;
        opt.dataset['id'] = entry.id;
        opt.dataset['source'] = entry.source;
        if (entry.splatLocalUrl) opt.dataset['splatUrl'] = entry.splatLocalUrl;
        if (entry.panoLocalUrl) opt.dataset['panoUrl'] = entry.panoLocalUrl;
        if (entry.glbLocalUrl) opt.dataset['glbUrl'] = entry.glbLocalUrl;
        const date = new Date(entry.createdAt).toLocaleDateString();
        const tag = entry.glbLocalUrl
          ? '[3D]'
          : entry.panoLocalUrl
            ? '[Pano]'
            : '[Splat]';
        const label = `${tag} ${date} — ${entry.prompt.slice(0, 50)}${entry.prompt.length > 50 ? '…' : ''}`;
        opt.textContent = label;
        splatSelectEl.appendChild(opt);
      }
    } catch (err) {
      console.warn('[world-cache] failed to load world cache:', err);
    }
  })();

  /**
   * Apply the assets for a selected dropdown option to the scene. Returns
   * a Promise<void> that resolves when the asset has finished loading,
   * or `false` when the option had no usable URLs (e.g. the empty default
   * option). The promise return lets the prompt-form submit await cached
   * loads and surface their progress in the unified loading panel; the
   * dropdown change handler ignores the promise (it doesn't show loading
   * UI for mid-session swaps).
   *
   * Splat loads return an immediately-resolved promise because SplatMesh
   * decodes asynchronously without exposing a load promise — we treat it
   * as "instant" from the loading-panel's POV and rely on the user seeing
   * the splat materialise behind the dismissed card.
   */
  function loadCachedEntryFromOption(opt: HTMLOptionElement | null): Promise<void> | false {
    if (!opt) return false;
    const glbUrl = opt.dataset['glbUrl'];
    const panoUrl = opt.dataset['panoUrl'];
    const splatUrl = opt.dataset['splatUrl'];
    if (glbUrl) {
      clearSplat();
      // GLB already carries textured hull — never overlay GroundedSkybox (async
      // pano loads used to finish after mesh and recreated the spherical dome).
      // When the cache row has a pano too, use it as scene.background inside
      // the hull (Skybox-viewer style) for dropdown testing.
      return loadGroundMesh(glbUrl, panoUrl || undefined);
    }
    if (panoUrl) {
      clearSplat();
      clearGround();
      return loadPanoramaBackground(panoUrl);
    }
    if (splatUrl) {
      clearGround();
      const flipX = opt.dataset['source'] === 'worldlabs';
      loadSplat(splatUrl, flipX);
      return Promise.resolve();
    }
    return false;
  }

  splatSelectEl?.addEventListener('change', () => {
    const opt = splatSelectEl?.options[splatSelectEl.selectedIndex] ?? null;
    const result = loadCachedEntryFromOption(opt);
    if (result === false) clearWorld();
    // Mid-session change — no loading UI to update. The promise (when
    // present) is intentionally not awaited; the user sees the asset
    // appear when it's ready.
  });

  // ── Prompt form submit ──────────────────────────────────────────────────
  if (promptFormEl) {
    promptFormEl.addEventListener('submit', (e) => {
      e.preventDefault();
      const prompt = promptTextareaEl?.value.trim() ?? '';
      if (!prompt) return;
      if (promptErrorEl) promptErrorEl.classList.add('hidden');
      if (debugPromptEl) debugPromptEl.textContent = prompt;

      // Initialise the AudioContext NOW, inside the user-gesture handler.
      // Browsers block AudioContext creation in async callbacks fired after
      // the gesture window closes (e.g. when SSE 'playing' arrives seconds later).
      void ensureNarrationReady();
      ambientPlayer.prepareForSession();

      // Hide the overlay so the scene (character, prior environment) is
      // visible while loading happens. Loading state is communicated by
      // the bottom-centre pill alone. The env *pill* is gated on
      // sequentialDone so it doesn't take over before connect/plan/
      // narration have had their min-visibility — but the scene itself
      // is never hidden.
      promptOverlayEl?.classList.add('hidden');
      sequentialDone = false;
      setLoadingStep(1);
      if (promptSubmitEl) {
        promptSubmitEl.disabled = true;
        promptSubmitEl.textContent = 'Preparing…';
      }

      void (async () => {
        // Block here until the GPU animation server is confirmed ready.
        // Abort with a specific message on any failure — never fall through to director.
        const serverStatus = await waitForAnimationServer();
        if (serverStatus !== 'ready') {
          const messages: Record<string, string> = {
            error:       'Animation server encountered an error loading the model. Please try again shortly.',
            timeout:     'Animation server took too long to start. Please try again in a moment.',
            unreachable: 'Could not reach the director server. Is it running?',
          };
          abortLoading(messages[serverStatus] ?? 'Animation server unavailable.');
          return;
        }

        // Hidden env dropdown is for dev reload only (change handler). Every
        // prompt Begin requests a fresh Skybox job — never skip_world_gen here.
        if (splatSelectEl) splatSelectEl.value = '';
        director.start(prompt, /* skipWorldGen */ false, currentVoiceId, currentGender, biasMode).catch((err) =>
          console.error('[main] director start error:', err),
        );
      })();
      // Vocalizations are now per-beat (pre-generated server-side). No scene-wide narration.
    });
  }

  // Prompt button — always visible. Stops any active director session and
  // re-shows the prompt overlay so the user can start fresh or refine.
  // No-op if the prompt overlay is already showing.
  if (repromptBtnEl) {
    repromptBtnEl.addEventListener('click', () => {
      director.stop();
      ambientPlayer.stop();
      worldLoader.stop();
      clearWorld();
      if (splatSelectEl) splatSelectEl.value = '';
      // Wipe every pill so the form re-opens with no stale loading state.
      clearAllLoading();
      setColdStart(false);
      // Hide debug (shows why-pill) while the prompt form is open
      setDebugVisible(false);
      if (debugPromptEl) debugPromptEl.textContent = '—';
      if (promptSubmitEl) {
        promptSubmitEl.disabled = false;
        promptSubmitEl.textContent = 'Begin';
      }
      promptOverlayEl?.classList.remove('hidden');
    });
  }

  // Dismiss the prompt overlay without starting the director — lets the user
  // browse animations manually via the dropdown.
  if (promptCloseEl) {
    promptCloseEl.addEventListener('click', () => {
      promptOverlayEl?.classList.add('hidden');
      // Restore debug if it was active before the prompt opened
      if (!beatPlayer['active']) beatPlayer.start();
    });
  }

  // ── Heart-rate model ────────────────────────────────────────────────────
  // Derived vital sign: target BPM is computed from the current beat's
  // emotion + intensity + energy and smoothed toward over ~1.8s with a
  // small HRV jitter. Resting baseline is 65 BPM; arousal-class emotions
  // (fear/anger/excitement) push up, parasympathetic states (calm/sad)
  // pull down, and kinetic energy adds on top regardless of valence.
  //
  // The numbers are deliberately illustrative rather than physiologically
  // exact — the goal is a believable, responsive HUD vital that animates
  // when the scene escalates. The CSS heart-pulse dot's animationDuration
  // is rebound from this value so the visual pulse keeps in sync.
  //
  // Declared BEFORE `beatPlayer.start()` because that call synchronously
  // emits an onStateChange that flows into updateDebugOverlay →
  // recomputeHeartTarget. Putting these consts after start() trips a
  // temporal-dead-zone ReferenceError on first paint.
  const HR_BASELINE_BPM = 65;
  const HR_BY_EMOTION: Record<string, number> = {
    neutral: 0,
    calm: -8, bored: -5, tired: -10,
    joy: +6, excitement: +20, triumphant: +14,
    sadness: -4, defeated: -2,
    anger: +22, disgust: +5, tense: +14,
    fear: +28, surprise: +16, disbelief: +8, confused: +4,
  };
  const HR_SMOOTH_SEC = 1.8;
  let hrTargetBpm = HR_BASELINE_BPM;
  let hrCurrentBpm = HR_BASELINE_BPM;

  function recomputeHeartTarget(beat: Beat | null): void {
    if (!beat) {
      hrTargetBpm = HR_BASELINE_BPM;
      return;
    }
    const emotionShift =
      (HR_BY_EMOTION[beat.emotion] ?? 0) * (beat.intensity ?? 0.5);
    const energyShift = (beat.energy ?? 0.3) * 14;
    hrTargetBpm = Math.max(45, Math.min(160, HR_BASELINE_BPM + emotionShift + energyShift));
  }

  // Also start the idle loop immediately on page load so the character
  // moves even before any prompt is submitted. (Must come AFTER the
  // heart-rate model — see TDZ note above.)
  beatPlayer.start();

  // ── Debug overlay update ─────────────────────────────────────────────────
  // Two paths into the overlay:
  //   - updateDebugOverlay(state): called from the BeatPlayer's onStateChange
  //     callback whenever the current beat changes. Updates the *beat-bound*
  //     fields (clip name, why, source, emotion, energy, gaze, gesture, pose).
  //   - the 100ms vitals tick below: updates the *time-varying* fields
  //     (queue depth, elapsed, heart, breath, timescale, refill latency,
  //     active gesture from figure, active holds from beat-player).
  // Splitting them lets the heart pulse animate smoothly without piggybacking
  // on the much-rarer beat-change events.
  function updateDebugOverlay(state: { current: Beat | null; queueDepth: number; inGap: boolean; elapsedSec: number; mirrored: boolean }): void {
    if (!debugOverlayEl) return;
    const beat = state.current;
    if (debugBeatEl) debugBeatEl.textContent = beat?.clip ?? '—';
    if (debugDurationEl) {
      debugDurationEl.textContent = beat
        ? (beat.duration_sec != null ? `${beat.duration_sec.toFixed(1)}s` : 'natural')
        : '—';
    }
    if (debugReasonEl) debugReasonEl.textContent = beat?.reason ?? '—';
    if (debugClipReasonEl) debugClipReasonEl.textContent = beat?.clip_reason ?? '—';
    if (debugClipMatchEl) debugClipMatchEl.textContent = beat?.clip_match ?? '—';
    // Keep the why-pill in sync regardless of which view is shown.
    if (whyPillEl) whyPillEl.textContent = beat?.reason ?? '';
    if (debugSourceEl) debugSourceEl.textContent = beat?.source ?? '—';
    if (debugEmotionEl) {
      debugEmotionEl.textContent = beat
        ? `${beat.emotion} · ${(beat.intensity ?? 0).toFixed(2)}`
        : '—';
    }
    if (debugEnergyEl) {
      debugEnergyEl.textContent = beat ? (beat.energy ?? 0).toFixed(2) : '—';
    }
    if (debugGazeEl) debugGazeEl.textContent = beat?.gaze ?? 'drift';
    if (debugPoseEl) debugPoseEl.textContent = beat?.is_idle ? 'idle' : (beat ? 'emotive' : '—');
    if (debugFadeEl) {
      debugFadeEl.textContent = beat?.fade_sec != null ? `${beat.fade_sec.toFixed(2)}s` : '—';
    }
    if (debugMirroredEl) debugMirroredEl.textContent = state.mirrored ? 'yes' : 'no';
    // Recompute the heart-rate target on every beat change so escalation
    // (e.g. surprise → fear) lands quickly. The smoothing in the vitals
    // tick still prevents a hard jump.
    recomputeHeartTarget(beat);
  }

  // ── Vitals tick (100ms) ────────────────────────────────────────────────
  // Drives every "live" field in the overlay at 10Hz. Cheap because all the
  // sources are already in memory — no DOM thrash beyond textContent writes
  // on the few elements that actually changed value.
  const VITALS_INTERVAL_MS = 100;
  setInterval(() => {
    if (!debugVisible) return;

    const dtSec = VITALS_INTERVAL_MS / 1000;
    // Smooth the heart rate toward target (exponential lerp). Add a small
    // ±0.6 BPM HRV jitter so the digit doesn't appear frozen even when the
    // scene is steady.
    const hrJitter = (Math.random() - 0.5) * 1.2;
    const hrK = 1 - Math.exp(-dtSec / HR_SMOOTH_SEC);
    hrCurrentBpm += (hrTargetBpm + hrJitter - hrCurrentBpm) * hrK;

    if (debugHeartEl) debugHeartEl.textContent = `${Math.round(hrCurrentBpm)} BPM`;
    if (debugHeartPulseEl) {
      // CSS heart-beat keyframe is one beat per cycle; sync animationDuration
      // to actual BPM so the pulse visually matches the number.
      debugHeartPulseEl.style.animationDuration = `${(60 / Math.max(30, hrCurrentBpm)).toFixed(2)}s`;
    }

    const breath = figure.getBreathingState();
    if (debugBreathEl) {
      debugBreathEl.textContent = `${breath.bpm.toFixed(1)} BPM · amp ${breath.amplitude.toFixed(2)}`;
    }

    if (debugTimescaleEl) {
      debugTimescaleEl.textContent = `${figure.getCurrentTimeScale().toFixed(2)}×`;
    }

    // Active gesture comes from the figure (auto-clears when its envelope
    // expires) so the HUD reflects what the user actually sees on the face.
    if (debugGestureEl) {
      debugGestureEl.textContent = figure.getActiveGesture() ?? '—';
    }
    if (debugBodyGestureEl) {
      debugBodyGestureEl.textContent = figure.getActiveBodyGesture() ?? '—';
    }
    if (debugBodyStateEl) {
      debugBodyStateEl.textContent = figure.getBodyState();
    }

    // Active hold list reflects the additive layers currently blending.
    // We union the figure's procedural-hold names (the source of truth for
    // pose composition) with the BeatPlayer's tracked names (which include
    // GLB-backed holds the figure tracks in a separate slot). Falls back to
    // em-dash when nothing is held.
    if (debugHoldsEl) {
      const procedural = figure.getActiveProceduralHolds();
      const tracked = beatPlayer.activeHoldNames;
      const union = Array.from(new Set([...procedural, ...tracked]));
      debugHoldsEl.textContent = union.length === 0 ? '—' : union.join(', ');
    }

    const playerState = beatPlayer.state;
    if (debugQueueEl) {
      debugQueueEl.textContent = `${playerState.queueDepth} queued${playerState.inGap ? ' (gap)' : ''}`;
    }
    if (debugElapsedEl) {
      debugElapsedEl.textContent = `${playerState.elapsedSec.toFixed(0)}s`;
    }
    if (debugMirroredEl) debugMirroredEl.textContent = playerState.mirrored ? 'yes' : 'no';

    // Session-level numbers (only meaningful while the director is active;
    // for the manual mode they all show 0 / "—" which is correct).
    const info = director.sessionInfo;
    if (debugBeatsEl) debugBeatsEl.textContent = String(info.beatsPlayed);
    if (debugPaidEl) debugPaidEl.textContent = String(info.paidBeats);
    if (debugRefillEl) {
      if (info.lastRefillSec === 0) {
        debugRefillEl.textContent = '—';
        debugRefillEl.removeAttribute('data-stale');
      } else {
        debugRefillEl.textContent = `${info.lastRefillSec.toFixed(0)}s ago`;
        // Flag a stalled refill at >25s — the continue endpoint normally
        // returns within ~5–35s. Sustained values past this hint at server
        // latency or a Uthana hang worth investigating.
        debugRefillEl.dataset.stale = info.lastRefillSec > 25 ? 'true' : 'false';
      }
    }
  }, VITALS_INTERVAL_MS);

  // ── Debug toggle (D key) + face expression test (0–4 keys) ─────────────
  let debugVisible = false;

  // ARKit preset expressions for testing blendshape control on GLB characters.
  const EXPRESSION_PRESETS: Record<string, Record<string, number>> = {
    '1': { mouthSmileLeft: 0.8, mouthSmileRight: 0.8, cheekSquintLeft: 0.4, cheekSquintRight: 0.4 },
    '2': { mouthFrownLeft: 0.7, mouthFrownRight: 0.7, browDownLeft: 0.6, browDownRight: 0.6, browInnerUp: 0.4 },
    '3': { jawOpen: 0.6, browOuterUpLeft: 0.8, browOuterUpRight: 0.8, eyeWideLeft: 0.7, eyeWideRight: 0.7 },
    '4': { browDownLeft: 0.8, browDownRight: 0.8, noseSneerLeft: 0.5, noseSneerRight: 0.5, mouthPressLeft: 0.4, mouthPressRight: 0.4 },
    '0': {},
  };

  document.addEventListener('keydown', (e) => {
    // Ignore hotkeys while the user is typing in any input or textarea
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;

    if (e.key === 'd' || e.key === 'D') {
      setDebugVisible(!debugVisible);
    }

    // C — toggle the cache browser modal.
    if ((e.key === 'c' || e.key === 'C') && cacheBrowser) {
      e.preventDefault();
      cacheBrowser.toggle();
    }

    // T — toggle the live pose-tuning panel.
    if (e.key === 't' || e.key === 'T') {
      e.preventDefault();
      tuningPanel.toggle();
      if (tuneBtnEl) tuneBtnEl.textContent = tuningPanel.isOpen ? 'Close tune' : 'Tune';
    }

    // M — toggle the manual Scene Director panel. (D is debug overlay.)
    if (e.key === 'm' || e.key === 'M') {
      e.preventDefault();
      sceneDirectorPanel.toggle();
      if (directBtnEl) directBtnEl.textContent = sceneDirectorPanel.isOpen ? 'Close direct' : 'Direct';
    }

    // Expression test keys — only active when character has blendshapes
    if (EXPRESSION_PRESETS[e.key] !== undefined && figure.morphTargetNames.length > 0) {
      figure.setExpression(EXPRESSION_PRESETS[e.key]);
      console.log(`[expression] preset ${e.key || 'neutral'}`, EXPRESSION_PRESETS[e.key]);
    }
  });

  // ── Manual animation playback ───────────────────────────────────────────
  // The bottom-left animation picker UI was removed; the director now owns
  // playback. The figure still exposes playClipFromUrl/queueClipFromUrl for
  // console debugging (e.g. `window.__figure.playClipFromUrl(url)`), so the
  // underlying machinery remains intact even with no UI hooked up.

  // ── Breathing HUD (hidden; code kept for future use) ────────────────────
  const bpmSliderEl = document.getElementById('breath-bpm') as HTMLInputElement | null;
  const ampSliderEl = document.getElementById('breath-amp') as HTMLInputElement | null;
  const bpmValueEl = document.getElementById('breath-bpm-value');
  const ampValueEl = document.getElementById('breath-amp-value');
  const bpmLiveEl = document.getElementById('breath-bpm-live');
  const ampLiveEl = document.getElementById('breath-amp-live');
  const presetsEl = document.getElementById('breath-presets');

  if (bpmSliderEl) bpmSliderEl.value = String(BREATHING_BPM_DEFAULT);
  if (ampSliderEl) ampSliderEl.value = String(Math.round(BREATHING_AMP_DEFAULT * 100));
  if (bpmValueEl) bpmValueEl.textContent = String(BREATHING_BPM_DEFAULT);
  if (ampValueEl) ampValueEl.textContent = BREATHING_AMP_DEFAULT.toFixed(2);

  function pushBreathTargetFromSliders(): void {
    if (!bpmSliderEl || !ampSliderEl) return;
    const bpm = Number(bpmSliderEl.value);
    const amplitude = Number(ampSliderEl.value) / 100;
    if (bpmValueEl) bpmValueEl.textContent = String(bpm);
    if (ampValueEl) ampValueEl.textContent = amplitude.toFixed(2);
    currentBreathBpm = bpm;
    currentBreathAmp = amplitude;
    figure
      .setBreathing({ bpm, amplitude })
      .catch((err) => console.warn('[main] setBreathing failed', err));
  }

  bpmSliderEl?.addEventListener('input', pushBreathTargetFromSliders);
  ampSliderEl?.addEventListener('input', pushBreathTargetFromSliders);

  if (presetsEl) {
    presetsEl.replaceChildren(
      ...BREATHING_PRESETS.map((p) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'breath-preset';
        btn.textContent = p.label;
        btn.title = `${p.bpm} BPM · amp ${p.amplitude}`;
        btn.addEventListener('click', () => {
          if (bpmSliderEl) bpmSliderEl.value = String(p.bpm);
          if (ampSliderEl) ampSliderEl.value = String(Math.round(p.amplitude * 100));
          pushBreathTargetFromSliders();
        });
        return btn;
      }),
    );
  }

  if (breathHudEl) {
    setInterval(() => {
      const live = figure.getBreathingState();
      if (bpmLiveEl) bpmLiveEl.textContent = live.bpm.toFixed(1);
      if (ampLiveEl) ampLiveEl.textContent = live.amplitude.toFixed(2);
    }, 120);
  }

  // First-paint clip — kicks the figure off on the default idle so the
  // character is breathing/moving before the prompt is even submitted.
  // The director (once started) takes over via beatPlayer.
  if (defaultEntry) {
    figure
      .playClipFromUrl(defaultEntry.url, 0)
      .catch((err) => console.warn('[main] failed to play default clip', err));
  }

  // ── Camera controls ──────────────────────────────────────────────────────
  const clock = new Clock();
  const CAMERA_FOLLOW_HEIGHT = 1.0;
  const BODY_CAM_Y = CAMERA_FOLLOW_HEIGHT;
  let targetCamY = BODY_CAM_Y;

  renderer.domElement.style.cursor = 'grab';

  // ── Camera preset buttons (Face / Body) ──────────────────────────────────
  const camBodyBtn = document.getElementById('cam-body-btn') as HTMLButtonElement | null;
  const camFaceBtn = document.getElementById('cam-face-btn') as HTMLButtonElement | null;

  function setCamPreset(preset: 'body' | 'face'): void {
    if (hullSkyboxMode) {
      if (preset === 'face') applyHullFaceCamera();
      else resetHullBodyCamera();
    } else if (preset === 'face') {
      applyProfileFaceCamera();
    } else {
      targetCamY = BODY_CAM_Y;
      resetProfileBodyCamera();
    }
    if (preset === 'face') {
      camFaceBtn?.classList.add('active');
      camBodyBtn?.classList.remove('active');
    } else {
      camBodyBtn?.classList.add('active');
      camFaceBtn?.classList.remove('active');
    }
  }

  camBodyBtn?.addEventListener('click', () => setCamPreset('body'));
  camFaceBtn?.addEventListener('click', () => setCamPreset('face'));
  camBodyBtn?.classList.add('active');
  applyProfileBodyDefault();
  orbitControls.saveState();

  // ── Camera follow ────────────────────────────────────────────────────
  //
  // During uthana_locomotion beats the figure translates through world
  // space (root motion is preserved on those clips — see beat-player.ts
  // and figure.fetchClip). To keep the character on screen while they
  // walk, the camera target smoothly tracks the figure's hip world
  // position. Smoothing constant CAMERA_FOLLOW_LERP is small enough
  // that single-frame jitter doesn't shake the camera, but big enough
  // that a 2-stride spurt is visibly followed.
  //
  // SNAP threshold: between locomotion beats the hip-position track is
  // rebased to bind position, which can cause a sudden jump from
  // (bindHip + Δ) back to bindHip. If we lerped slowly through that
  // jump the figure would briefly leave the frame. CAMERA_SNAP_DIST is
  // the world-space distance above which we snap the camera target to
  // the figure instead of lerping — keeping the figure on screen at
  // the cost of a single-frame camera cut.
  //
  // Y is locked to a constant (CAMERA_FOLLOW_HEIGHT) so the figure's
  // breathing/head-bob doesn't translate into camera bob — only X/Z
  // tracking. The camera's offset from target is preserved across
  // tracking, so the figure stays at the same screen position the
  // user composed via drag/zoom.
  const CAMERA_FOLLOW_LERP = 0.18;
  const CAMERA_SNAP_DIST = 0.6;
  const figureWorldHip = new Vector3();

  const tick = (): void => {
    const dt = clock.getDelta();
    figure.update(dt);
    figure.root.rotation.y = baseYaw;

    if (hullSkyboxMode) {
      syncHullShadowLight();
      if (camPreset === 'face') {
        getFaceLookAt(hullTripodTarget, HULL_CHARACTER_SCALE);
        orbitControls.target.copy(hullTripodTarget);
        target.copy(hullTripodTarget);
      }
    } else if (camPreset !== 'face') {
      figure.getHipWorldPosition(figureWorldHip);
      const dx = figureWorldHip.x - target.x;
      const dz = figureWorldHip.z - target.z;
      const dist = Math.hypot(dx, dz);
      if (dist > CAMERA_SNAP_DIST) {
        target.x = figureWorldHip.x;
        target.z = figureWorldHip.z;
      } else {
        target.x += dx * CAMERA_FOLLOW_LERP;
        target.z += dz * CAMERA_FOLLOW_LERP;
      }
      target.y += (targetCamY - target.y) * 0.08;
      orbitControls.target.copy(target);
    }

    orbitControls.update();

    // Keep the near-wall exclusion sphere centred on the camera every frame.
    // SplatEdit applies GPU-side to all SplatMeshes — no shader hacking needed.
    cameraExclusionSphere.position.copy(camera.position);

    // Drive lip-sync blend shapes from HeadAudio viseme analysis.
    narrationPlayer.update(dt * 1000);

    renderer.render(scene, camera);

    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);

  window.addEventListener(
    'resize',
    () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    },
    { passive: true },
  );
}

boot().catch(console.error);
